// One stock in depth (Groww's stock page): hero, chart with 1D/1M ranges, tabs for the read, levels,
// events and peers, and a sticky trade-plan card with the shortlist. Live or simulated like the board.
import { Link } from '@/lib/nav';
import { useParams, useSearchParams } from '@/lib/nav';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon, LinkIcon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { NSE, detail, simHistory, snapshot, type DailyCandle } from '@planb/core';
import { fmtPct } from '@/charts/board';
import { since, useJournal } from '@/lib/client/use-journal';
import { lastBarForming, todayIso, useIndexStrip, useMarket } from '@/lib/client/use-market';
import { useResolvedTheme } from '@/lib/client/use-me';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AppHeader, Page } from './app-header';
import { carry, dateSeed, indexById, stockPath } from './common';
import { JournalBox, newEntry, sinceClass } from './journal';
import { IntradayChart, TIMEFRAMES, type ChartDesk } from './intraday-chart';
import { actionLabel, type CorpAction } from '@/lib/corp-actions';
import { usePaperTrades } from './paper-trades';
import { openStock, runPageTool, setStockReady, usePageTools, waitFor } from '@/lib/desk/tools';
import type { PlanMark } from '@/charts/marks';
import { OpenInBroker } from './open-in-broker';
import { ReplayBar, useReplay } from './replay';
import { Events, Explain, HistoryStats, KeyNumbers, Levels, Peers, StockHero, TradePlan } from './stock-sections';
import { api } from '@/lib/api';
import { recordView } from '@/lib/recents';
import { FnoPanel, useFno } from './fno-panel';

export function StockApp() {
  const { symbol } = useParams<{ symbol: string }>();
  const sym = decodeURIComponent(symbol ?? '').toUpperCase();
  // A fresh view per symbol, so its replay position and history start clean.
  return <StockView key={sym} symbol={sym} />;
}

function StockView({ symbol }: { symbol: string }) {
  const params = useSearchParams();
  const chosen = indexById(params.get('index'));
  // Rank within the chosen index if the stock is in it, else the broadest index that has it.
  const ix = chosen?.members.includes(symbol) ? chosen
    : [indexById('NIFTY500'), indexById('FNO')].find((i) => i?.members.includes(symbol)) ?? chosen ?? indexById('FNO')!;
  const seed = Number(params.get('seed')) || dateSeed();
  const { session, status, mode, live, version, user, role } = useMarket({ index: ix.id, seed });
  const strip = useIndexStrip(session, live, version);
  const journal = useJournal();
  const [history, setHistory] = useState<DailyCandle[] | null>(null);

  const board = useMemo(() => {
    if (!session) return null;
    const members = new Set(ix.members);
    return { ...session, stocks: session.stocks.filter((s) => members.has(s.symbol)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, ix, version]);
  const raw = board?.stocks.find((s) => s.symbol === symbol);
  const replay = useReplay(board?.stocks[0]?.bars.length ?? 0, 200);
  const theme = useResolvedTheme();
  const tt = replay.tt;
  const forming = lastBarForming(mode, tt, replay.n);
  const snap = useMemo(() => (board && tt ? snapshot(NSE, board, tt, { lastBarForming: forming }) : null), [board, tt, forming]);
  const a = snap?.stocks.find((s) => s.symbol === symbol);
  const d = useMemo(() => (raw && a && snap ? detail(raw, a, tt, snap.stocks, { lastBarForming: forming }) : null), [raw, a, tt, snap, forming]);
  const rank = a && snap ? snap[a.bull ? 'bulls' : 'bears'].findIndex((s) => s.symbol === symbol) + 1 : 0;
  const date = session?.date || todayIso();
  const prevDay = useMemo(() => (raw ? { high: raw.prevHigh, low: raw.prevLow, close: raw.prevClose } : undefined), [raw?.prevHigh, raw?.prevLow, raw?.prevClose]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = carry({ index: chosen?.id, live, seed });
  const href = (s: string) => stockPath(s, query);

  // Daily history: from the feed's database when live, simulated otherwise.
  useEffect(() => {
    if (!raw) return;
    if (!live) { setHistory(simHistory(raw, seed, date)); return; }
    let stop = false;
    api(`/v1/history?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((h) => { if (!stop) setHistory(h); })
      .catch(() => { if (!stop) setHistory([]); });
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, symbol, seed, date, !!raw]);


  useEffect(() => { document.title = `${symbol} · MoneyHQ`; recordView(symbol); }, [symbol]);

  // This stock's paper trades, drawn on its chart.
  const { rows: paperRows } = usePaperTrades();
  const myPaper = useMemo(() => (paperRows ?? []).filter((r) => r.trade.symbol === symbol), [paperRows, symbol]);

  // This stock's corporate actions (splits, bonuses, dividends): marked on the days chart, and read by
  // the desk (an upcoming ex-date is event risk for a trade held across it).
  const [corp, setCorp] = useState<{ upcoming: CorpAction[]; recent: CorpAction[] } | null>(null);
  useEffect(() => {
    let stop = false;
    setCorp(null);
    void api(`/v1/corporate-actions?symbol=${encodeURIComponent(symbol)}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (!stop && j) setCorp({ upcoming: j.upcoming ?? [], recent: j.recent ?? [] }); }).catch(() => {});
    return () => { stop = true; };
  }, [symbol]);
  const allActions = useMemo(() => [...(corp?.recent ?? []), ...(corp?.upcoming ?? [])], [corp]);

  // ---- The desk agent's tools on this page: read it, switch its chart, mark a paper plan.
  const chartDesk = useRef<ChartDesk | null>(null);
  const loaded = !!(a && d && snap);
  useEffect(() => {
    if (!loaded) return;
    let stop = false;
    void waitFor(() => !!chartDesk.current?.ready(), 20000).then(() => { if (!stop) setStockReady(symbol); }, () => { if (!stop) setStockReady(symbol); });
    return () => { stop = true; setStockReady(null); };
  }, [loaded, symbol]);
  const fno = useFno(symbol, live && mode !== 'closed');
  const readPage = () => {
    if (!a || !d || !snap) return { page: 'stock', symbol, note: 'still loading' };
    const p = d.plan;
    const num = (x: number) => +x.toFixed(2);
    let capital: string | null = null, riskPct: string | null = null;
    try { capital = localStorage.getItem('mhq.capital'); riskPct = localStorage.getItem('mhq.riskPct'); } catch { /* not stored */ }
    return {
      page: 'stock', symbol, sector: a.sector,
      data: live ? (mode === 'closed' ? `real NSE data, market closed (${date} ${snap.time})` : `real NSE data, live (${date} ${snap.time})`) : 'simulated practice session',
      price: a.ltp, changePct: num(a.pct),
      today: {
        prevClose: a.prevClose, open: a.open, high: a.high, low: a.low, changePoints: num(a.ltp - a.prevClose),
        dayRangePct: num(((a.high - a.low) / a.prevClose) * 100), usualDayRangePct: raw?.avgDayRangePct != null ? num(raw.avgDayRangePct) : null,
        volumeVsUsualForTimeOfDay: `${a.relVol.toFixed(2)}×`,
        futuresOpenInterest: a.hasOI ? { changePct: num(a.oiChg), buildUp: a.buildUp } : 'no futures contract',
        vwap: num(a.vwap), priceVsVwap: a.ltp >= a.vwap ? 'above' : 'below',
        orderFlow: a.flow ? { note: 'estimated from candles (buy vs sell volume)', cumulativeDelta: Math.round([...a.flow.cvd].reverse().find((v) => v != null) ?? 0) } : null,
        sector: { name: a.sector, avgMovePct: num(a.sectorAvg), up: `${a.sectorAdv} of ${a.sectorN}`, movingWithSector: a.withSector },
        // When in the day the move happened (to line catalysts up with it): the low and high, and
        // the change from the previous close at each hour's last 5-minute candle.
        timeline: (() => {
          const bs = d.bars, chg = (c: number) => `${c >= a.prevClose ? '+' : ''}${num(((c - a.prevClose) / a.prevClose) * 100)}%`;
          if (!bs.length) return null;
          let lo = 0, hi = 0;
          bs.forEach((b, j) => { if (b.l < bs[lo].l) lo = j; if (b.h > bs[hi].h) hi = j; });
          const hourly = bs.map((b, j) => ({ t: NSE.timeLabel(j), c: b.c, j })).filter((x, j, all) => j === all.length - 1 || all[j + 1].t.slice(0, 2) !== x.t.slice(0, 2))
            .map((x) => ({ until: NSE.timeLabel(x.j + 1), price: x.c, fromPrevClose: chg(x.c) }));
          return { dayLowAt: NSE.timeLabel(lo), dayHighAt: NSE.timeLabel(hi), byHour: hourly };
        })(),
      },
      todayBias: a.bull ? 'bullish (buyers in control so far)' : 'bearish (sellers in control so far)', strength: a.score,
      eventRisk: a.eventRisk,
      corporateActions: corp && (corp.upcoming.length || corp.recent.length) ? {
        upcoming: corp.upcoming.map((x) => ({ exDate: x.exDate, what: actionLabel(x) ?? x.subject, subject: x.subject, daysAway: Math.round((Date.parse(x.exDate) - Date.parse(date)) / 86400000) })),
        lastYear: corp.recent.slice(0, 4).map((x) => ({ exDate: x.exDate, what: actionLabel(x) ?? x.subject })),
        note: 'Chart prices are adjusted for splits and bonuses (marked at the ex-date, not a fall). Dividends are not: the price drops by about the dividend on its ex-date, and a trade held across it carries that.',
      } : null,
      chart: chartDesk.current?.state() ?? null,
      todayZonePlan: p && !a.eventRisk ? {
        side: p.side, zonePriceZ: num(p.z), stop: num(p.stop), target1: num(p.t1), target1Is: p.t1Name, target2: num(p.t2), target2Is: p.t2Name,
        riskPerShare: num(p.riskPerShare), rewardRisk: p.rr, state: p.state,
        tested: 'Intraday order-block zone entries had no edge after costs in MoneyHQ\'s test (5-minute data, NSE 500); treat this plan as a level map, not an edge.',
      } : a.eventRisk ? 'withheld: event risk (moves like this do not respect zones)' : 'no active zone today',
      actNow: { stop: num(d.stop), target: num(d.target) },
      userSettings: (() => {
        const cap = capital ? Number(capital.replace(/[^\d.]/g, '')) || null : null;
        return { capital: cap, capitalText: cap ? `₹${cap.toLocaleString('en-IN')}` : 'not set', riskPctPerTrade: riskPct ? Number(riskPct) : null };
      })(),
      timeframesAvailable: live ? TIMEFRAMES : TIMEFRAMES.filter((x) => /m|h/.test(x)),
      fno: !a.hasOI ? 'no futures or options' : fno.view ? {
        note: 'From Kite quotes; IV computed by MoneyHQ (Black-76). Context, not a signal: give each figure with its tested record.',
        future: fno.view.future ?? null, lotSize: fno.view.lotSize ?? null,
        iv30Pct: fno.view.iv30 ?? null, ivRank: fno.view.ivRank != null ? `higher than ${Math.floor(fno.view.ivRank)}% of the last ${fno.view.ivRankSessions} sessions` : null,
        expiries: fno.view.expiries.map((e) => ({ expiry: e.expiry, days: e.days, forward: e.forward, atmStrike: e.atmStrike, atmIVPct: e.atmIV ?? null, movePricedInPct: e.straddlePct ?? null, straddle: e.straddle ?? null, putCallOIRatio: e.pcrOI ?? null })),
        futuresBuildUpToday: a.buildUp, tested: fno.view.tested,
      } : fno.error ?? 'loading',
    };
  };
  usePageTools({
    read_page: () => readPage(),
    show_chart: async (args) => {
      const want = String(args.symbol ?? '').trim().toUpperCase();
      if (want && want !== symbol) { await openStock(want); return runPageTool('show_chart', { timeframe: args.timeframe }); }
      if (args.timeframe) {
        if (!chartDesk.current?.switchTo(String(args.timeframe))) return { error: `Unknown or unavailable timeframe ${String(args.timeframe)}; use one of ${TIMEFRAMES.join(', ')}` };
        await new Promise((r) => setTimeout(r, 50)); // let the switch render before waiting on its data
        await waitFor(() => !!chartDesk.current?.ready(), 20000);
        await new Promise((r) => setTimeout(r, 300)); // patterns are computed right after the candles draw
      }
      return readPage();
    },
    mark_chart: (args) => {
      const levels = (Array.isArray(args.levels) ? args.levels : []) as PlanMark[];
      chartDesk.current?.mark(levels.map((l) => ({ price: Number(l.price), label: String(l.label ?? ''), kind: String(l.kind ?? 'entry') })));
      return { ok: true, marked: levels.length };
    },
  });

  const entry = journal.find(date, symbol);
  const past = journal.entries.filter((e) => e.symbol === symbol && e.date !== date);

  // Phones get the compact set on the name row (the row above the hero is for wider screens).
  const actionsFor = (compact: boolean) => a && snap && (
    <>
      <OpenInBroker symbol={symbol} query={query} compact={compact} />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size={compact ? 'icon' : 'icon-lg'} className="rounded-full" aria-label="Copy link"
            onClick={() => { void navigator.clipboard?.writeText(location.href).then(() => toast.success('Link copied')); }}>
            <LinkIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Copy link</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant={entry ? 'default' : 'outline'} size={compact ? 'icon' : 'icon-lg'} className="rounded-full" aria-pressed={!!entry}
            aria-label={entry ? 'Remove from shortlist' : 'Add to shortlist'}
            onClick={() => (entry ? journal.remove(entry.id) : journal.add(newEntry(a, date, snap.time)))}>
            <StarIcon className={cn(entry && 'fill-current')} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{entry ? 'Remove from shortlist' : 'Add to shortlist'}</TooltipContent>
      </Tooltip>
    </>
  );
  const actions = actionsFor(false);

  return (
    <>
      <AppHeader section={null} mode={mode} status={status} user={user} role={role} query={query} strip={strip} />
      <Page>
        <div className="mb-4 flex min-h-9 items-center justify-between gap-3 max-sm:hidden">
          {/* Phones: the tab bar does the navigating and the actions sit on the hero's name row. */}
          <nav aria-label="Breadcrumb" className="hidden items-center gap-1.5 text-sm text-muted-foreground sm:flex">
            <Link href={`/${query}`} className="hover:text-ink">Market Flow</Link>
            <ChevronRightIcon className="size-3.5" />
            <span aria-current="page" className="font-medium text-ink">{symbol}</span>
          </nav>
          {actions && <div className="flex gap-2">{actions}</div>}
        </div>
        {!a || !d || !snap ? (
          <p className="py-16 text-center text-muted-foreground">
            {session ? `${symbol} isn't in the data${live ? ' loaded by the feed yet' : ''}. Try the search (⌘K).` : 'Loading…'}
          </p>
        ) : (
          <div className="@container">
          {/* Two columns when the page itself is wide enough (the desk docked beside it takes width). */}
          <div className="grid items-start gap-7 @5xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0">
              <StockHero a={a} rank={rank} time={`${live ? (mode === 'closed' ? 'Close' : 'Live') : 'Simulated'} · ${date} ${snap.time}`}
                aside={<ReplayBar r={replay} time={snap.time} showSpeed={false} compact />} actions={actionsFor(true)} />

              <Card className="mt-5 gap-0 py-0 max-sm:mt-3">
                <IntradayChart a={a} d={d} t={tt} symbol={symbol} date={date} prev={prevDay} live={live} deskRef={chartDesk} paper={myPaper} actions={allActions}
                  replay={<ReplayBar r={replay} time={snap.time} showSpeed={false} />}
                  legend={<>
                    <span><b className="text-ink">SH / SL</b>: today’s swing highs and lows (MoneyHQ’s levels)</span>
                    <span>· <b className="text-ink">P, R1–R3, S1–S3</b>: classic pivots from the previous session, as on Groww and TradingView</span>
                    <span>· <b className="text-ink">Z</b>: the zone price · purple: liquidity (PDH/PDL, ORH/ORL, round numbers) · arrows: sweeps · right-edge bars: volume profile (POC: the busiest price) · Δ / CVD: estimated order flow</span>
                  </>} />
                {history && history.length > 0 && <div className="border-t pb-3"><HistoryStats a={a} hist={history} usualPct={raw?.avgDayRangePct} /></div>}
              </Card>

              <Tabs defaultValue="overview" className="mt-7">
                <TabsList variant="line" className="group-data-horizontal/tabs:h-11 w-full justify-start gap-2 overflow-x-auto overflow-y-hidden border-b p-0 [scrollbar-width:none]">
                  {['Overview', 'Levels', 'Events', 'Peers', ...(a.hasOI ? ['F&O'] : [])].map((t) => (
                    <TabsTrigger key={t} value={t.toLowerCase()}
                      className="h-full flex-none px-3.5 text-[15px] data-active:text-brand-strong after:bg-primary group-data-horizontal/tabs:after:bottom-[-1px]">{t}</TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="overview" className="grid gap-5 pt-3">
                  <Card><CardHeader><CardTitle className="text-base font-semibold">Key numbers</CardTitle></CardHeader>
                    <CardContent><KeyNumbers a={a} d={d} /></CardContent></Card>
                  <Card><CardHeader><CardTitle className="text-base font-semibold">What this means</CardTitle></CardHeader>
                    <CardContent><Explain a={a} d={d} t={tt} />
                      <p className="mt-3 text-xs text-muted-foreground/80">Calculated from today&apos;s 5-minute candles and live ticks. For learning only, not investment advice.</p>
                    </CardContent></Card>
                </TabsContent>
                <TabsContent value="levels" className="pt-3">
                  <Card className="gap-0 py-0"><Levels a={a} d={d} /></Card>
                </TabsContent>
                <TabsContent value="events" className="pt-3">
                  <Card><CardHeader><CardTitle className="text-base font-semibold">Today&apos;s events</CardTitle></CardHeader>
                    <CardContent><Events a={a} d={d} /></CardContent></Card>
                </TabsContent>
                {a.hasOI && (
                  <TabsContent value="f&o" className="pt-3">
                    <FnoPanel symbol={symbol} view={fno.view} error={fno.error} buildUp={a.buildUp} oiChgPct={a.oiChg} />
                  </TabsContent>
                )}
                <TabsContent value="peers" className="pt-3">
                  <p className="mb-3 text-sm text-muted-foreground">{a.sector} in {ix.name} · {a.sectorAdv} of {a.sectorN} up · sector {fmtPct(a.sectorAvg)}</p>
                  <Card className="gap-0 py-0"><Peers a={a} snap={snap} href={href} /></Card>
                </TabsContent>
              </Tabs>
            </div>

            <aside className="grid gap-5 @5xl:sticky @5xl:top-[136px]">
              <TradePlan a={a} d={d} time={snap.time}>
                <JournalBox a={a} date={date} time={snap.time} entry={entry} add={journal.add} update={journal.update} remove={journal.remove} />
              </TradePlan>
              {past.length > 0 && (
                <Card className="gap-0 py-0">
                  <CardHeader className="border-b py-4"><CardTitle className="text-[15px] font-semibold">Earlier entries</CardTitle></CardHeader>
                  {past.map((e) => {
                    const s = since(e, a.ltp);
                    return (
                      <div key={e.id} className="border-b border-border/60 px-4 py-3 text-[13px] last:border-b-0">
                        <b className="text-ink">{e.date} {e.time}</b> · {e.side === 'bull' ? 'bullish' : 'bearish'} at {NSE.currencySymbol}{NSE.formatPrice(e.priceAtAdd)}
                        {' '}<span className={cn('num', sinceClass(s.cls))}>now {fmtPct(s.chg)}</span>
                        {e.note && <p className="mt-1 text-muted-foreground"><span className="text-ink">Why:</span> {e.note}</p>}
                        {e.review && <p className="mt-1 text-muted-foreground"><span className="text-ink">Review:</span> {e.review}</p>}
                      </div>
                    );
                  })}
                </Card>
              )}
            </aside>
          </div>
          </div>
        )}
      </Page>
    </>
  );
}
